import React, { useEffect, useMemo, useState } from "react";
import { Form } from "antd";

import Icon from "../../../components/AppIcon";
import { AutoSaveIndicator } from "../../../utils/formDataProtection";
import NotesModal from "./NotesModal";
import LoanDocumentsModal from "./dashboard/LoanDocumentsModal";
import {
  normalizeLoanBreakupCustomFields,
  sumLoanBreakupCustomFields,
} from "./loan-form/shared/loanBreakupFields";

const hasValue = (v) =>
  v !== undefined && v !== null && !(typeof v === "string" && v.trim() === "");
const AUTO_LOAN_NUMBER_REGEX = /^LN-\d{4}-\d+$/i;

const pick = (...vals) => vals.find((v) => hasValue(v));

const asNumber = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const isFinanceYes = (v) => {
  if (v === true) return true;
  const t = String(v ?? "")
    .trim()
    .toLowerCase();
  return t === "yes" || t === "y" || t === "true" || t === "1";
};

const isAutoGeneratedLoanNumber = (value) =>
  AUTO_LOAN_NUMBER_REGEX.test(String(value || "").trim());

const hasDisbursedStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .includes("disburs");

const formatMoney = (v) => {
  const n = asNumber(v);
  if (!n) return "";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
};

const formatPercent = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${n}%`;
};

const stagePillClass = (stage) => {
  const s = String(stage || "").toLowerCase();
  if (s.includes("disbursed")) {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300";
  }
  if (s.includes("approved")) {
    return "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/50 dark:text-cyan-300";
  }
  if (s.includes("pending")) {
    return "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300";
  }
  if (s.includes("reject")) {
    return "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300";
  }
  return "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300";
};

const LoanStickyHeader = ({
  title,
  activeStep,
  isFinanced,
  form,
  innerRef,
  autoSaveStatus = null,
}) => {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  // Base watches
  const loanId = Form.useWatch("loanId", form);
  const loanNumber = Form.useWatch("loan_number", form);
  const customerName = Form.useWatch("customerName", form);
  const primaryMobile = Form.useWatch("primaryMobile", form);
  const mobileNo = Form.useWatch("mobileNo", form);
  const customerEmail = Form.useWatch("customerEmail", form);
  const email = Form.useWatch("email", form);

  const approvalStatus = Form.useWatch("approval_status", form);
  const disbursementStatus = Form.useWatch("disbursement_status", form);
  const disbursementDate = Form.useWatch("disbursement_date", form);
  const approvalBanksData = Form.useWatch("approval_banksData", form);
  const approvalBankName = Form.useWatch("approval_bankName", form);
  const approvalLoanAmountApproved = Form.useWatch(
    "approval_loanAmountApproved",
    form,
  );
  const approvalLoanAmountDisbursed = Form.useWatch(
    "approval_loanAmountDisbursed",
    form,
  );
  const approvalBreakupNet = Form.useWatch(
    "approval_breakup_netLoanApproved",
    form,
  );
  const approvalBreakupCredit = Form.useWatch(
    "approval_breakup_creditAssured",
    form,
  );
  const approvalBreakupInsurance = Form.useWatch(
    "approval_breakup_insuranceFinance",
    form,
  );
  const approvalBreakupEw = Form.useWatch("approval_breakup_ewFinance", form);
  const approvalBreakupCustom = Form.useWatch("approval_breakup_custom", form);
  const postfileLoanAmountApproved = Form.useWatch(
    "postfile_loanAmountApproved",
    form,
  );
  const postfileLoanAmountDisbursed = Form.useWatch(
    "postfile_loanAmountDisbursed",
    form,
  );
  const postfileDisbursedLoan = Form.useWatch("postfile_disbursedLoan", form);
  const postfileDisbursedCredit = Form.useWatch(
    "postfile_disbursedCreditAssured",
    form,
  );
  const postfileDisbursedInsurance = Form.useWatch(
    "postfile_disbursedInsurance",
    form,
  );
  const postfileDisbursedEw = Form.useWatch("postfile_disbursedEw", form);
  const postfileDisbursedCustom = Form.useWatch(
    "postfile_disbursed_custom",
    form,
  );
  const formLoanAmount = Form.useWatch("loanAmount", form);
  const financeExpectation = Form.useWatch("financeExpectation", form);
  const postfileRoi = Form.useWatch("postfile_roi", form);
  const approvalRoi = Form.useWatch("approval_interestRate", form);
  const approvalRoiRaw = Form.useWatch("approval_roi", form);
  const approvalTenureMonths = Form.useWatch("approval_tenureMonths", form);
  const postfileTenureMonths = Form.useWatch("postfile_tenureMonths", form);
  const postfileEmiAmount = Form.useWatch("postfile_emiAmount", form);
  const approvalEmiAmount = Form.useWatch("approval_emiAmount", form);

  const typeOfLoan = Form.useWatch("typeOfLoan", form);
  const caseType = Form.useWatch("caseType", form);
  const loanType = Form.useWatch("loanType", form);
  const vehicleMake = Form.useWatch("vehicleMake", form);
  const vehicleModel = Form.useWatch("vehicleModel", form);
  const vehicleVariant = Form.useWatch("vehicleVariant", form);
  const exShowroomPrice = Form.useWatch("exShowroomPrice", form);
  const showroomDealerName = Form.useWatch("showroomDealerName", form);
  const vehicleRegNo = Form.useWatch("vehicleRegNo", form);
  const yearOfManufacture = Form.useWatch("yearOfManufacture", form);

  const resolvedLoanKey = String(pick(loanNumber, loanId, title, "__new__"));
  const [snapshot, setSnapshot] = useState({});
  const primaryBankRow = useMemo(() => {
    const banks = Array.isArray(approvalBanksData) ? approvalBanksData : [];
    if (!banks.length) return null;
    return (
      banks.find(
        (b) => String(b?.status || "").toLowerCase() === "disbursed",
      ) ||
      banks.find((b) => String(b?.status || "").toLowerCase() === "approved") ||
      banks[0]
    );
  }, [approvalBanksData]);

  // Reset snapshot when loan changes
  useEffect(() => {
    setSnapshot({});
  }, [resolvedLoanKey]);

  // Collect current live values with fallback probes
  const live = useMemo(() => {
    const finFlag = pick(isFinanced, form?.getFieldValue?.("isFinanced"));
    return {
      customerName: pick(
        customerName,
        form?.getFieldValue?.("customerName"),
        title,
        "New Application",
      ),
      mobile: pick(
        primaryMobile,
        mobileNo,
        form?.getFieldValue?.("primaryMobile"),
        form?.getFieldValue?.("mobileNo"),
      ),
      email: pick(
        customerEmail,
        email,
        form?.getFieldValue?.("customerEmail"),
        form?.getFieldValue?.("email"),
      ),
      loanRef: pick(
        loanNumber,
        loanId,
        form?.getFieldValue?.("loan_number"),
        form?.getFieldValue?.("loanId"),
      ),
      stage: pick(approvalStatus, activeStep, "Pending"),
      disbursementStatus: pick(
        disbursementStatus,
        form?.getFieldValue?.("disbursement_status"),
      ),
      disbursementDate: pick(
        disbursementDate,
        form?.getFieldValue?.("disbursement_date"),
        form?.getFieldValue?.("approval_disbursedDate"),
      ),
      bank: pick(
        approvalBankName,
        primaryBankRow?.bankName,
        form?.getFieldValue?.("approval_bankName"),
      ),
      roi: pick(
        postfileRoi,
        approvalRoiRaw,
        approvalRoi,
        primaryBankRow?.interestRate,
        form?.getFieldValue?.("postfile_roi"),
        form?.getFieldValue?.("approval_roi"),
        form?.getFieldValue?.("approval_interestRate"),
      ),
      tenure: pick(
        postfileTenureMonths,
        approvalTenureMonths,
        primaryBankRow?.tenure,
        form?.getFieldValue?.("postfile_tenureMonths"),
        form?.getFieldValue?.("approval_tenureMonths"),
      ),
      emi: pick(
        postfileEmiAmount,
        approvalEmiAmount,
        primaryBankRow?.emiAmount,
        form?.getFieldValue?.("postfile_emiAmount"),
        form?.getFieldValue?.("approval_emiAmount"),
      ),
      caseType: pick(
        caseType,
        typeOfLoan,
        loanType,
        form?.getFieldValue?.("caseType"),
        form?.getFieldValue?.("typeOfLoan"),
        form?.getFieldValue?.("loanType"),
      ),
      vehicleMake: pick(vehicleMake, form?.getFieldValue?.("vehicleMake")),
      vehicleModel: pick(vehicleModel, form?.getFieldValue?.("vehicleModel")),
      vehicleVariant: pick(
        vehicleVariant,
        form?.getFieldValue?.("vehicleVariant"),
      ),
      exShowroom: pick(
        exShowroomPrice,
        form?.getFieldValue?.("exShowroomPrice"),
      ),
      showroomDealerName: pick(
        showroomDealerName,
        form?.getFieldValue?.("showroomDealerName"),
      ),
      regNo: pick(vehicleRegNo, form?.getFieldValue?.("vehicleRegNo")),
      year: pick(yearOfManufacture, form?.getFieldValue?.("yearOfManufacture")),
      isFinancedRaw: finFlag,
      isFinancedCase: isFinanceYes(finFlag),
      approvalBreakupTotal:
        asNumber(
          approvalBreakupNet ??
            form?.getFieldValue?.("approval_breakup_netLoanApproved"),
        ) +
        asNumber(
          approvalBreakupCredit ??
            form?.getFieldValue?.("approval_breakup_creditAssured"),
        ) +
        asNumber(
          approvalBreakupInsurance ??
            form?.getFieldValue?.("approval_breakup_insuranceFinance"),
        ) +
        asNumber(
          approvalBreakupEw ??
            form?.getFieldValue?.("approval_breakup_ewFinance"),
        ) +
        sumLoanBreakupCustomFields(
          normalizeLoanBreakupCustomFields(
            approvalBreakupCustom ??
              form?.getFieldValue?.("approval_breakup_custom") ??
              [],
          ),
        ),
      postfileDisbursedBreakupTotal:
        asNumber(
          postfileDisbursedLoan ??
            form?.getFieldValue?.("postfile_disbursedLoan"),
        ) +
        asNumber(
          postfileDisbursedCredit ??
            form?.getFieldValue?.("postfile_disbursedCreditAssured"),
        ) +
        asNumber(
          postfileDisbursedInsurance ??
            form?.getFieldValue?.("postfile_disbursedInsurance"),
        ) +
        asNumber(
          postfileDisbursedEw ?? form?.getFieldValue?.("postfile_disbursedEw"),
        ) +
        sumLoanBreakupCustomFields(
          normalizeLoanBreakupCustomFields(
            postfileDisbursedCustom ??
              form?.getFieldValue?.("postfile_disbursed_custom") ??
              [],
          ),
        ),
      disbursedAmount: pick(
        postfileLoanAmountDisbursed,
        primaryBankRow?.disbursedAmount,
        primaryBankRow?.loanAmount,
        approvalLoanAmountDisbursed,
        postfileLoanAmountApproved,
        formLoanAmount,
        form?.getFieldValue?.("postfile_loanAmountDisbursed"),
        form?.getFieldValue?.("approval_loanAmountDisbursed"),
        form?.getFieldValue?.("disburse_amount"),
        form?.getFieldValue?.("disburseAmount"),
        form?.getFieldValue?.("postfile_loanAmountApproved"),
        form?.getFieldValue?.("loanAmount"),
      ),
      approvedAmount: pick(
        approvalLoanAmountApproved,
        approvalLoanAmountDisbursed,
        postfileLoanAmountApproved,
        primaryBankRow?.loanAmount,
        formLoanAmount,
        form?.getFieldValue?.("approval_loanAmountApproved"),
        form?.getFieldValue?.("approval_loanAmountDisbursed"),
        form?.getFieldValue?.("postfile_loanAmountApproved"),
        form?.getFieldValue?.("loanAmount"),
      ),
      expectedAmount: pick(
        financeExpectation,
        form?.getFieldValue?.("financeExpectation"),
      ),
    };
  }, [
    activeStep,
    approvalBankName,
    approvalBanksData,
    approvalLoanAmountApproved,
    approvalLoanAmountDisbursed,
    approvalBreakupNet,
    approvalBreakupCredit,
    approvalBreakupInsurance,
    approvalBreakupEw,
    approvalBreakupCustom,
    postfileDisbursedLoan,
    postfileDisbursedCredit,
    postfileDisbursedInsurance,
    postfileDisbursedEw,
    postfileDisbursedCustom,
    postfileLoanAmountApproved,
    approvalRoi,
    approvalRoiRaw,
    approvalStatus,
    approvalTenureMonths,
    postfileTenureMonths,
    caseType,
    customerEmail,
    customerName,
    disbursementDate,
    disbursementStatus,
    email,
    exShowroomPrice,
    financeExpectation,
    form,
    isFinanced,
    loanId,
    loanNumber,
    loanType,
    mobileNo,
    postfileEmiAmount,
    approvalEmiAmount,
    formLoanAmount,
    postfileLoanAmountDisbursed,
    postfileRoi,
    primaryMobile,
    title,
    typeOfLoan,
    vehicleMake,
    vehicleModel,
    showroomDealerName,
    vehicleRegNo,
    vehicleVariant,
    yearOfManufacture,
    primaryBankRow,
  ]);

  // Persist latest known non-empty values; live values always override
  useEffect(() => {
    setSnapshot((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(live).forEach(([k, v]) => {
        if (!hasValue(v)) return;
        if (prev[k] !== v) {
          next[k] = v;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [live]);

  const data = {
    ...snapshot,
    ...Object.fromEntries(Object.entries(live).filter(([, v]) => hasValue(v))),
  };

  const normalizedCaseType = String(data.caseType || "")
    .trim()
    .toLowerCase();
  const normalizedFinanceFlag = String(data.isFinancedRaw || "")
    .trim()
    .toLowerCase();
  const isDisbursedCase =
    hasDisbursedStatus(data.disbursementStatus) ||
    hasDisbursedStatus(data.stage) ||
    hasDisbursedStatus(primaryBankRow?.status) ||
    hasValue(data.disbursementDate) ||
    asNumber(data.disbursedAmount) > 0;
  // Only pure cash-sale/cash-car cases should hide finance terms.
  // "Car Cash-in" is a financed flow and must still show Loan Terms.
  const isCashCaseByType =
    normalizedFinanceFlag === "no" ||
    normalizedCaseType === "cash car" ||
    normalizedCaseType === "cash sale" ||
    normalizedCaseType === "cash";
  const actualLoanRef = isAutoGeneratedLoanNumber(data.loanRef)
    ? ""
    : data.loanRef;
  const loanCorePrimaryLabel = isCashCaseByType
    ? "Cash Home"
    : data.isFinancedCase && hasValue(data.bank)
      ? data.bank
      : actualLoanRef || "-";
  const isNewCar = data.caseType === "New Car";
  const loanAmount = (() => {
    const disb = asNumber(data.disbursedAmount);
    const appr = asNumber(data.approvedAmount);
    const apprBreakup = asNumber(data.approvalBreakupTotal);
    const postBreakup = asNumber(data.postfileDisbursedBreakupTotal);
    const expected = asNumber(data.expectedAmount);
    if (isDisbursedCase) {
      return (
        disb ||
        postBreakup ||
        appr ||
        apprBreakup ||
        expected ||
        pick(data.disbursedAmount, data.approvedAmount, data.expectedAmount)
      );
    }
    const strongest = Math.max(
      disb,
      appr,
      apprBreakup,
      postBreakup,
      expected,
      0,
    );
    return strongest || pick(data.disbursedAmount, data.approvedAmount, data.expectedAmount);
  })();
  const loanAmountLabel = isDisbursedCase
    ? "Loan Disbursed"
    : hasValue(data.approvedAmount)
      ? "Loan Approved"
      : "Loan Expected";
  const shouldShowLoanTerms =
    !isCashCaseByType &&
    (data.isFinancedCase ||
      hasValue(data.bank) ||
      asNumber(loanAmount) > 0 ||
      hasValue(data.roi) ||
      hasValue(data.tenure) ||
      hasValue(data.emi));

  // Only actual vehicle-backed loans (e.g. "New Vehicle") have showroom/reg-no
  // data captured — a property-purchase case has none of that, so the
  // subtitle below must not show "Regd:/Year:" for it.
  const isVehicleBackedLoan = /vehicle|car/.test(normalizedCaseType);

  const vehicleLine = data.typeOfLoan || data.caseType || "";

  const loanForDocumentsModal = useMemo(() => {
    const formValues = form?.getFieldsValue?.(true) || {};
    return {
      ...formValues,
      _id: pick(formValues?._id, loanId),
      loanId: pick(formValues?.loanId, loanId),
      loan_number: pick(formValues?.loan_number, loanNumber),
      customerName: pick(formValues?.customerName, data.customerName),
    };
  }, [form, loanId, loanNumber, data.customerName]);

  return (
    <>
      <div
        ref={innerRef}
        className="fixed left-0 right-0 z-[100] w-full border-b border-border bg-background/95 backdrop-blur-sm shadow-sm"
        style={{ top: 60 }}
      >
        <div className="w-full px-2 sm:px-3 md:px-4 py-2">
          <div className="flex items-stretch overflow-x-auto rounded-2xl border border-slate-300/70 bg-white/90 shadow-[0_14px_28px_-20px_rgba(15,23,42,0.35)] no-scrollbar dark:border-slate-700/80 dark:bg-slate-950/85">
            <div className="min-w-[230px] sm:min-w-[250px] px-3 py-2 bg-sky-50/65 dark:bg-sky-950/15">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-sky-700 dark:text-sky-300">
                <Icon name="User" size={11} />
                Customer
              </p>
              {hasValue(data.customerName) && (
                <p className="truncate text-[13px] font-semibold text-slate-900 dark:text-slate-100">
                  {data.customerName}
                </p>
              )}
              <p className="mt-0.5 truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">
                {[data.mobile, data.email]
                  .filter((v) => hasValue(v))
                  .join(" • ")}
              </p>
            </div>

            <div className="flex items-center bg-gradient-to-r from-sky-50/65 to-emerald-50/65 px-1.5 dark:from-sky-950/15 dark:to-emerald-950/15">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-sky-200 bg-sky-100/80 text-sky-700 dark:border-sky-900/70 dark:bg-sky-950/30 dark:text-sky-300">
                <Icon name="ChevronRight" size={12} />
              </span>
            </div>

            <div className="min-w-[260px] sm:min-w-[280px] px-3 py-2 bg-emerald-50/65 dark:bg-emerald-950/15">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300">
                <Icon name="Landmark" size={11} />
                Loan Core
              </p>
              <p className="truncate text-[13px] font-semibold text-slate-900 dark:text-slate-100">
                {loanCorePrimaryLabel}
              </p>
              <p className="mt-0.5 truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">
                {[hasValue(actualLoanRef) ? `Loan#: ${actualLoanRef}` : null]
                  .filter(Boolean)
                  .join(" • ")}
              </p>
            </div>

            {shouldShowLoanTerms && (
              <>
                <div className="flex items-center bg-gradient-to-r from-emerald-50/65 to-teal-50/65 px-1.5 dark:from-emerald-950/15 dark:to-teal-950/15">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-emerald-200 bg-emerald-100/80 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300">
                    <Icon name="ChevronRight" size={12} />
                  </span>
                </div>
                <div className="min-w-[250px] sm:min-w-[270px] px-3 py-2 bg-teal-50/65 dark:bg-teal-950/15">
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-teal-700 dark:text-teal-300">
                    <Icon name="Calculator" size={11} />
                    Loan Terms
                  </p>
                  <p className="truncate text-[13px] font-semibold text-slate-900 dark:text-slate-100">
                    {hasValue(loanAmount)
                      ? `${loanAmountLabel}: ${formatMoney(loanAmount)}`
                      : "-"}
                  </p>
                  <p className="mt-0.5 truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">
                    {[
                      hasValue(data.roi) ? formatPercent(data.roi) : null,
                      hasValue(data.tenure) ? `${data.tenure}M` : null,
                      hasValue(data.emi) ? formatMoney(data.emi) : null,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </p>
                </div>
              </>
            )}

            <div className="flex items-center bg-gradient-to-r from-teal-50/65 to-violet-50/65 px-1.5 dark:from-teal-950/15 dark:to-violet-950/15">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-violet-200 bg-violet-100/80 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/30 dark:text-violet-300">
                <Icon name="ChevronRight" size={12} />
              </span>
            </div>

            <div className="min-w-[280px] sm:min-w-[300px] px-3 py-2 bg-violet-50/65 dark:bg-violet-950/15">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-violet-700 dark:text-violet-300">
                <Icon name="Building2" size={11} />
                Property
              </p>
              <p className="truncate text-[13px] font-semibold text-slate-900 dark:text-slate-100">
                {vehicleLine || "-"}
              </p>
              <p className="mt-0.5 truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">
                {isVehicleBackedLoan
                  ? (isNewCar
                      ? [
                          hasValue(data.showroomDealerName)
                            ? data.showroomDealerName
                            : null,
                          hasValue(data.exShowroom)
                            ? formatMoney(data.exShowroom)
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" • ")
                      : [
                          hasValue(data.regNo) ? `Regd: ${data.regNo}` : null,
                          hasValue(data.year) ? `Year: ${data.year}` : null,
                        ]
                          .filter(Boolean)
                          .join(" • ")) || "-"
                  : "-"}
              </p>
            </div>

            <div className="flex items-center bg-gradient-to-r from-violet-50/65 to-amber-50/65 px-1.5 dark:from-violet-950/15 dark:to-amber-950/15">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-200 bg-amber-100/80 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300">
                <Icon name="ChevronRight" size={12} />
              </span>
            </div>

            <div className="min-w-[200px] sm:min-w-[220px] px-3 py-2 bg-amber-50/65 dark:bg-amber-950/15">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">
                <Icon name="BadgeCheck" size={11} />
                Status
              </p>
              <p className="truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">
                {data.caseType || "-"}
              </p>
              <div className="mt-0.5 flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${stagePillClass(data.stage)}`}
                >
                  {data.stage}
                </span>
                <AutoSaveIndicator status={autoSaveStatus} />
              </div>
            </div>

            <div className="border-l border-slate-300/80 dark:border-slate-700/80" />
            <div className="flex min-w-[132px] flex-col justify-center gap-1 px-2 py-1.5">
              <button
                onClick={() => setShowDocumentsModal(true)}
                className="flex items-center justify-center gap-1 rounded-md border border-cyan-200/70 bg-cyan-50/80 px-2 py-1.5 text-cyan-700 transition-colors hover:bg-cyan-100 dark:border-cyan-900/70 dark:bg-cyan-950/20 dark:text-cyan-300 dark:hover:bg-cyan-950/35"
                title="Documents"
              >
                <Icon name="Files" size={13} />
                <p className="text-[10px] font-semibold">Documents</p>
              </button>
              <button
                onClick={() => setShowNotesModal(true)}
                className="flex items-center justify-center gap-1 rounded-md border border-violet-200/70 bg-violet-50/80 px-2 py-1.5 text-violet-700 transition-colors hover:bg-violet-100 dark:border-violet-900/70 dark:bg-violet-950/20 dark:text-violet-300 dark:hover:bg-violet-950/35"
                title="Notes"
              >
                <Icon name="MessageSquare" size={13} />
                <p className="text-[10px] font-semibold">Notes</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      <NotesModal
        open={showNotesModal}
        form={form}
        title={data.customerName || "Applicant"}
        onClose={() => setShowNotesModal(false)}
      />

      <LoanDocumentsModal
        loan={loanForDocumentsModal}
        open={showDocumentsModal}
        onClose={() => setShowDocumentsModal(false)}
      />
    </>
  );
};

export default LoanStickyHeader;
